import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";


import { updateOrderItem } from "../../../../../services/Admin/OrdersApi";

const normalizeTimeForInput = (value) => {
	if (!value) return "";
	const text = String(value).trim();
	const match = text.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
	if (!match) return "";
	return `${match[1]}:${match[2]}`;
};

const formatTime = (value) => {
	if (!value) return null;
	const text = String(value).trim();

	// input from <TextField type="time"/> غالبا HH:mm
	const hhmm = text.match(/^(\d{2}):(\d{2})$/);
	if (hhmm) return `${hhmm[1]}:${hhmm[2]}:00`;

	// already HH:mm:ss
	const hhmmss = text.match(/^(\d{2}):(\d{2}):(\d{2})$/);
	if (hhmmss) return text;

	return null;
};

const EditeItemsDialog = ({ open, onClose, item, onUpdated }) => {
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [successMsg, setSuccessMsg] = useState("");

	const [form, setForm] = useState({
		quantity: "",
		note: "",
		status: "",
		open_time: "",
		close_time: "",
		price: "",
	});

	// Load item data when dialog opens or item changes
	useEffect(() => {
		if (open && item) {
			setForm({
				quantity: item.quantity || "",
				note: item.note || "",
				status: item.status || "pending",
				open_time: normalizeTimeForInput(item.open_time),
				close_time: normalizeTimeForInput(item.close_time),
				price: item.price || "",
			});
			setError("");
			setSuccessMsg("");
		}
	}, [open, item]);

	// Reset form when dialog closes
	useEffect(() => {
		if (!open) {
			setForm({
				quantity: "",
				note: "",
				status: "",
				open_time: "",
				close_time: "",
				price: "",
			});
			setError("");
			setSuccessMsg("");
		}
	}, [open]);

	const handleChange = (key) => (e) => {
		setForm((prev) => ({ ...prev, [key]: e.target.value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccessMsg("");

		const parsedId = Number.parseInt(item?.id, 10);
		const parsedQuantity = Number.parseInt(form.quantity, 10);
		const parsedPrice = Number.parseFloat(form.price);
		const formattedOpenTime = formatTime(form.open_time);
		const formattedCloseTime = formatTime(form.close_time);

		// Validation
		if (!Number.isInteger(parsedId) || parsedId <= 0) {
			setError("معرف العنصر مطلوب");
			return;
		}
		if (!Number.isInteger(parsedQuantity) || parsedQuantity <= 0) {
			setError("الكمية يجب أن تكون أكبر من صفر");
			return;
		}
		if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
			setError("السعر غير صحيح");
			return;
		}
		if (!form.status) {
			setError("حالة الصنف مطلوبة");
			return;
		}
		if (form.open_time && !formattedOpenTime) {
			setError("تنسيق وقت البدء غير صحيح. المطلوب HH:mm أو HH:mm:ss");
			return;
		}
		if (form.close_time && !formattedCloseTime) {
			setError("تنسيق وقت الانتهاء غير صحيح. المطلوب HH:mm أو HH:mm:ss");
			return;
		}

		setSubmitting(true);
		try {
			const payload = {
				id: parsedId,
				quantity: parsedQuantity,
				note: form.note || "",
				status: String(form.status),
				open_time: formattedOpenTime,
				close_time: formattedCloseTime,
				price: parsedPrice,
			};

			const res = await updateOrderItem(payload);

			// Success: show message and notify parent
			setSuccessMsg(res?.message || "تم تعديل الصنف بنجاح");
			onUpdated && onUpdated(res?.data || null);

			// Close dialog after 1 second
			setTimeout(() => {
				onClose();
			}, 1000);
		} catch (err) {
			const serverMessage = err?.response?.data?.message;
			const serverErrors = err?.response?.data?.errors;

			let details = "";
			if (serverErrors && typeof serverErrors === "object") {
				details = Object.values(serverErrors)
					.flat()
					.filter(Boolean)
					.join(" | ");
			}

			setError(serverMessage || details || err?.message || "فشل تعديل الصنف");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>تعديل بيانات الصنف</DialogTitle>

			<DialogContent dividers>
				{successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				<Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, pt: 1 }}>
					{/* Product Name (Read-only) */}
					{item?.product?.name && (
						<TextField 
							label="المنتج" 
							value={item.product.name}
							fullWidth 
							disabled
							InputProps={{ readOnly: true }}
						/>
					)}

					<TextField 
						label="الكمية" 
						type="number"
						value={form.quantity} 
						onChange={handleChange("quantity")} 
						fullWidth 
						required
						inputProps={{ min: 1 }}
					/>

					<TextField 
						label="السعر" 
						type="number"
						value={form.price} 
						onChange={handleChange("price")} 
						disabled
						fullWidth 
						required
						inputProps={{ min: 0, step: 0.01 }}
						helperText="السعر ثابت (للعرض فقط)"
					/>

					<TextField 
						label="ملاحظات" 
						value={form.note} 
						onChange={handleChange("note")} 
						fullWidth 
						multiline 
						rows={2}
						placeholder="مثال: بدون سكر"
					/>

					<Alert severity="info">
						الحالة ووقت الفتح ووقت الإغلاق يتم التحكم بهم تلقائيا من النظام.
					</Alert>
				</Box>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>إلغاء</Button>
				<Button type="submit" onClick={handleSubmit} variant="contained" disabled={submitting}>
					{submitting ? <CircularProgress size={20} /> : "حفظ التعديلات"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditeItemsDialog;
